import { useState, useEffect } from 'react';
import { ExportButton } from '@/components/ExportButton';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Users, Plus, Search, Edit, Trash2, Phone, Mail, MapPin,
  Star, Package, CheckCircle2, X, Truck, TrendingUp, Eye, Building2
} from 'lucide-react';

interface Delegate {
  id: number;
  name: string;
  phone: string;
  email?: string;
  address?: string;
  city?: string;
  area?: string;
  branchId?: number;
  branch?: any;
  isActive: boolean;
  performance?: number;
  totalDeliveries?: number;
  rating?: number;
  createdAt: string;
  updatedAt?: string;
}

interface Branch {
  id: number;
  name: string;
}

export default function DelegatesPage() {
  const [delegates, setDelegates] = useState<Delegate[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingDelegate, setEditingDelegate] = useState<Delegate | null>(null);
  const [selectedDelegate, setSelectedDelegate] = useState<Delegate | null>(null);

  const exportColumns = [
    { key: 'name', label: 'الاسم' },
    { key: 'phone', label: 'الهاتف' },
    { key: 'email', label: 'البريد الإلكتروني' },
    { key: 'city', label: 'المدينة' },
    { key: 'area', label: 'المنطقة' },
    { key: 'totalDeliveries', label: 'عدد التوصيلات' },
    { key: 'rating', label: 'التقييم' },
    { key: 'isActive', label: 'الحالة', format: (v: any) => v ? 'نشط' : 'غير نشط' },
    { key: 'createdAt', label: 'تاريخ الانضمام', format: (v: any) => v ? new Date(v).toLocaleDateString('ar-EG') : '' }
  ];

  const [form, setForm] = useState({
    name: '', phone: '', email: '', address: '', city: '',
    area: '', branchId: '', isActive: true
  });

  useEffect(() => {
    loadDelegates();
    loadBranches();
  }, []);

  const loadBranches = async () => {
    try {
      const response = await api.get('/api/branches');
      if (response.data.success) {
        const data = response.data.data;
        setBranches(Array.isArray(data) ? data : (data?.items || []));
      }
    } catch (error) {
      console.error('Error loading branches:', error);
    }
  };

  const loadDelegates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/delegates?pageSize=100');
      if (response.data.success) {
        const data = response.data.data;
        setDelegates(Array.isArray(data) ? data : (data?.items || []));
      }
    } catch (error) {
      console.error('Error loading delegates:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingDelegate(null);
    setForm({
      name: '', phone: '', email: '', address: '', city: '',
      area: '', branchId: '', isActive: true
    });
    setShowModal(true);
  };

  const openEditModal = (delegate: Delegate) => {
    setEditingDelegate(delegate);
    setForm({
      name: delegate.name || '',
      phone: delegate.phone || '',
      email: delegate.email || '',
      address: delegate.address || '',
      city: delegate.city || '',
      area: delegate.area || '',
      branchId: delegate.branchId ? delegate.branchId.toString() : '',
      isActive: delegate.isActive !== false
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        branchId: form.branchId ? parseInt(form.branchId) : null
      };
      if (editingDelegate) {
        await api.put('/api/delegates/' + editingDelegate.id, payload);
        alert('تم تحديث بيانات المندوب بنجاح');
      } else {
        await api.post('/api/delegates', payload);
        alert('تم إضافة المندوب بنجاح');
      }
      setShowModal(false);
      loadDelegates();
    } catch (error: any) {
      alert(error.response?.data?.message || 'حدث خطأ في حفظ بيانات المندوب');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المندوب؟')) return;
    try {
      await api.delete('/api/delegates/' + id);
      loadDelegates();
      alert('تم حذف المندوب بنجاح');
    } catch (error: any) {
      alert(error.response?.data?.message || 'حدث خطأ في حذف المندوب');
    }
  };

  const filteredDelegates = Array.isArray(delegates) ? delegates.filter(d =>
    (d.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (d.phone || '').includes(search) ||
    (d.city || '').toLowerCase().includes(search.toLowerCase()) ||
    (d.area || '').toLowerCase().includes(search.toLowerCase())
  ) : [];

  const stats = {
    total: delegates.length,
    active: delegates.filter(d => d.isActive !== false).length,
    totalDeliveries: delegates.reduce((sum, d) => sum + (d.totalDeliveries || 0), 0),
    avgRating: delegates.filter(d => d.rating).length > 0
      ? (delegates.filter(d => d.rating).reduce((sum, d) => sum + (d.rating || 0), 0) / delegates.filter(d => d.rating).length).toFixed(1)
      : '0.0'
  };

  const inputClass = 'w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:bg-white transition';

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    for (let i = 0; i < 5; i++) {
      stars.push(
        <Star key={i} className={'w-4 h-4 ' + (i < fullStars ? 'text-orange-500 fill-orange-500' : 'text-gray-300')} />
      );
    }
    return stars;
  };

  return (
    <div className='space-y-6' dir='rtl'>
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900 flex items-center gap-3'>
            <div className='w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg'>
              <Truck className='w-6 h-6 text-white' />
            </div>
            <div>
              <span>إدارة المندوبين</span>
              <p className='text-sm text-gray-500 font-normal mt-1'>إدارة فريق المندوبين وتوزيع المهام</p>
            </div>
          </h1>
        </div>
        <ExportButton 
              data={delegates} 
              filename="delegates" 
              columns={exportColumns} 
              title="المندوبين"
            />
            <Button
          onClick={openCreateModal}
          className='bg-gradient-to-l from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg'
        >
          <Plus className='w-5 h-5 ml-2' />
          <span>إضافة مندوب جديد</span>
        </Button>
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
        <Card className='border-0 shadow-lg hover:shadow-xl transition'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-2'>
              <div className='w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center'>
                <Users className='w-5 h-5 text-orange-600' />
              </div>
            </div>
            <p className='text-sm text-gray-500'>إجمالي المندوبين</p>
            <p className='text-2xl font-bold text-gray-900'>{stats.total}</p>
          </CardContent>
        </Card>

        <Card className='border-0 shadow-lg hover:shadow-xl transition'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-2'>
              <div className='w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center'>
                <CheckCircle2 className='w-5 h-5 text-green-600' />
              </div>
            </div>
            <p className='text-sm text-gray-500'>المندوبون النشطون</p>
            <p className='text-2xl font-bold text-green-700'>{stats.active}</p>
          </CardContent>
        </Card>

        <Card className='border-0 shadow-lg hover:shadow-xl transition'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-2'>
              <div className='w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center'>
                <Package className='w-5 h-5 text-blue-600' />
              </div>
            </div>
            <p className='text-sm text-gray-500'>إجمالي التوصيلات</p>
            <p className='text-2xl font-bold text-blue-700'>{stats.totalDeliveries.toLocaleString()}</p>
          </CardContent>
        </Card>

        <Card className='border-0 shadow-lg hover:shadow-xl transition'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between mb-2'>
              <div className='w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center'>
                <Star className='w-5 h-5 text-orange-600' />
              </div>
            </div>
            <p className='text-sm text-gray-500'>متوسط التقييم</p>
            <div className='flex items-center gap-2'>
              <p className='text-2xl font-bold text-gray-900'>{stats.avgRating}</p>
              <div className='flex'>{renderStars(parseFloat(stats.avgRating))}</div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className='border-0 shadow-lg'>
        <div className='p-6'>
          <div className='flex flex-col md:flex-row md:items-center gap-4 mb-6'>
            <div className='flex-1 relative'>
              <Search className='absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400' />
              <input
                type='text'
                placeholder='بحث بالاسم، الهاتف، المدينة، أو المنطقة...'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className='w-full pr-10 pl-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-orange-500 focus:bg-white transition'
              />
            </div>
          </div>
          {loading ? (
            <div className='p-12 text-center'>
              <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto'></div>
              <p className='text-gray-500 mt-4'>جاري تحميل المندوبين...</p>
            </div>
          ) : filteredDelegates.length === 0 ? (
            <div className='p-12 text-center'>
              <Truck className='w-16 h-16 text-gray-300 mx-auto mb-4' />
              <h3 className='text-lg font-semibold text-gray-900 mb-2'>لا يوجد مندوبون</h3>
              <p className='text-gray-500'>ابدأ بإضافة مندوبك الأول</p>
            </div>
          ) : (
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead className='bg-gray-50 border-b border-gray-100'>
                  <tr>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>المندوب</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>بيانات التواصل</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>الموقع</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>الفرع</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>التوصيلات</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>التقييم</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>الحالة</th>
                    <th className='text-right py-4 px-5 text-sm font-semibold text-gray-600'>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDelegates.map((delegate) => (
                    <tr key={delegate.id} className='border-b border-gray-50 hover:bg-gray-50/50 transition'>
                      <td className='py-4 px-5'>
                        <div className='flex items-center gap-3'>
                          <div className='w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white font-bold'>
                            {(delegate.name || 'D').charAt(0)}
                          </div>
                          <div>
                            <p className='font-semibold text-gray-900'>{delegate.name}</p>
                            <p className='text-xs text-gray-500'>منذ {new Date(delegate.createdAt).toLocaleDateString('ar-EG')}</p>
                          </div>
                        </div>
                      </td>
                      <td className='py-4 px-5'>
                        <div className='space-y-1'>
                          <div className='flex items-center gap-2 text-sm text-gray-700'>
                            <Phone className='w-3.5 h-3.5 text-gray-400' />
                            <span>{delegate.phone || '-'}</span>
                          </div>
                          {delegate.email && (
                            <div className='flex items-center gap-2 text-sm text-gray-700'>
                              <Mail className='w-3.5 h-3.5 text-gray-400' />
                              <span>{delegate.email}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className='py-4 px-5'>
                        <div className='flex items-center gap-2 text-sm text-gray-700'>
                          <MapPin className='w-4 h-4 text-gray-400' />
                          <span>{delegate.city || '-'}{delegate.area ? ' - ' + delegate.area : ''}</span>
                        </div>
                      </td>
                      <td className='py-4 px-5'>
                        <div className='flex items-center gap-2 text-sm text-gray-700'>
                          <Building2 className='w-4 h-4 text-gray-400' />
                          <span>{delegate.branch?.name || '-'}</span>
                        </div>
                      </td>
                      <td className='py-4 px-5'>
                        <span className='font-semibold text-gray-900'>{delegate.totalDeliveries || 0}</span>
                      </td>
                      <td className='py-4 px-5'>
                        {delegate.rating ? (
                          <div className='flex items-center gap-1'>
                            <span className='font-semibold text-orange-600'>{delegate.rating}</span>
                            <div className='flex'>{renderStars(delegate.rating)}</div>
                          </div>
                        ) : (
                          <span className='text-gray-400'>-</span>
                        )}
                      </td>
                      <td className='py-4 px-5'>
                        <span className={'px-3 py-1 rounded-full text-xs font-semibold ' + (
                          delegate.isActive !== false ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
                        )}>
                          {delegate.isActive !== false ? 'نشط' : 'غير نشط'}
                        </span>
                      </td>
                      <td className='py-4 px-5'>
                        <div className='flex items-center gap-1'>
                          <button
                            onClick={() => { setSelectedDelegate(delegate); setShowViewModal(true); }}
                            className='p-2 hover:bg-orange-50 rounded-lg text-gray-500 hover:text-orange-600 transition'
                            title='عرض'
                          >
                            <Eye className='w-4 h-4' />
                          </button>
                          <button
                            onClick={() => openEditModal(delegate)}
                            className='p-2 hover:bg-blue-50 rounded-lg text-gray-500 hover:text-blue-600 transition'
                            title='تعديل'
                          >
                            <Edit className='w-4 h-4' />
                          </button>
                          <button
                            onClick={() => handleDelete(delegate.id)}
                            className='p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600 transition'
                            title='حذف'
                          >
                            <Trash2 className='w-4 h-4' />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {showModal && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50'>
          <Card className='w-full max-w-3xl max-h-[90vh] overflow-y-auto'>
            <div className='p-6'>
              <div className='flex items-center justify-between mb-4'>
                <h2 className='text-xl font-bold'>
                  {editingDelegate ? 'تعديل بيانات المندوب' : 'إضافة مندوب جديد'}
                </h2>
                <button onClick={() => setShowModal(false)} className='p-2 hover:bg-gray-100 rounded-lg transition'>
                  <X className='w-5 h-5' />
                </button>
              </div>
              <form onSubmit={handleSubmit} className='space-y-4'>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <div>
                    <Label>الاسم الكامل *</Label>
                    <Input
                      value={form.name}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, name: e.target.value })}
                      required
                      className={inputClass}
                      placeholder='أدخل اسم المندوب'
                    />
                  </div>
                  <div>
                    <Label>رقم الهاتف *</Label>
                    <Input
                      value={form.phone}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, phone: e.target.value })}
                      required
                      className={inputClass}
                      placeholder='01xxxxxxxxx'
                    />
                  </div>
                  <div>
                    <Label>البريد الإلكتروني</Label>
                    <Input
                      type='email'
                      value={form.email}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, email: e.target.value })}
                      className={inputClass}
                      placeholder='delegate@example.com'
                    />
                  </div>
                  <div>
                    <Label>الفرع</Label>
                    <select
                      value={form.branchId}
                      onChange={(e) => setForm({ ...form, branchId: e.target.value })}
                      className={inputClass}
                    >
                      <option value=''>اختر الفرع</option>
                      {branches.map(b => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>المدينة</Label>
                    <Input
                      value={form.city}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, city: e.target.value })}
                      className={inputClass}
                      placeholder='المدينة'
                    />
                  </div>
                  <div>
                    <Label>المنطقة</Label>
                    <Input
                      value={form.area}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, area: e.target.value })}
                      className={inputClass}
                      placeholder='المنطقة'
                    />
                  </div>
                  <div className='flex items-center gap-2 md:col-span-2'>
                    <input
                      type='checkbox'
                      id='isActive'
                      checked={form.isActive}
                      onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                      className='w-4 h-4'
                    />
                    <label htmlFor='isActive' className='text-sm font-medium'>مندوب نشط</label>
                  </div>
                </div>
                <div>
                  <Label>العنوان التفصيلي</Label>
                  <textarea
                    value={form.address}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setForm({ ...form, address: e.target.value })}
                    className={inputClass}
                    placeholder='العنوان التفصيلي'
                    rows={2}
                  />
                </div>
                <div className='flex gap-3 pt-4'>
                  <Button type='button' variant='outline' onClick={() => setShowModal(false)} className='flex-1'>إلغاء</Button>
                  <Button type='submit' className='flex-1 bg-gradient-to-l from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700'>
                    {editingDelegate ? 'تحديث البيانات' : 'إضافة المندوب'}
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}

      {showViewModal && selectedDelegate && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50'>
          <Card className='w-full max-w-2xl max-h-[90vh] overflow-y-auto'>
            <div className='p-6'>
              <div className='flex items-center justify-between mb-4'>
                <h2 className='text-xl font-bold'>تفاصيل المندوب</h2>
                <button onClick={() => setShowViewModal(false)} className='p-2 hover:bg-gray-100 rounded-lg'>
                  <X className='w-5 h-5' />
                </button>
              </div>
              <div className='space-y-4'>
                <div className='flex items-center gap-4 pb-4 border-b'>
                  <div className='w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white text-2xl font-bold'>
                    {(selectedDelegate.name || 'D').charAt(0)}
                  </div>
                  <div>
                    <h3 className='text-xl font-bold'>{selectedDelegate.name}</h3>
                    <p className='text-sm text-gray-500'>
                      {selectedDelegate.isActive !== false ? 'مندوب نشط' : 'مندوب غير نشط'} منذ {new Date(selectedDelegate.createdAt).toLocaleDateString('ar-EG')}
                    </p>
                  </div>
                </div>
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <p className='text-sm text-gray-500'>VIEW_رقم الهاتف *</p>
                    <p className='font-semibold'>{selectedDelegate.phone || '-'}</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>VIEW_البريد الإلكتروني</p>
                    <p className='font-semibold'>{selectedDelegate.email || '-'}</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>VIEW_المدينة</p>
                    <p className='font-semibold'>{selectedDelegate.city || '-'}</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>VIEW_المنطقة</p>
                    <p className='font-semibold'>{selectedDelegate.area || '-'}</p>
                  </div>
                  <div className='col-span-2'>
                    <p className='text-sm text-gray-500'>VIEW_العنوان التفصيلي</p>
                    <p className='font-semibold'>{selectedDelegate.address || '-'}</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>VIEW_الفرع</p>
                    <p className='font-semibold'>{selectedDelegate.branch?.name || '-'}</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>إجمالي التوصيلات</p>
                    <p className='font-semibold text-blue-600'>{selectedDelegate.totalDeliveries || 0}</p>
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>التقييم</p>
                    {selectedDelegate.rating ? (
                      <div className='flex items-center gap-2'>
                        <span className='font-semibold text-orange-600'>{selectedDelegate.rating}</span>
                        <div className='flex'>{renderStars(selectedDelegate.rating)}</div>
                      </div>
                    ) : (
                      <p className='font-semibold'>-</p>
                    )}
                  </div>
                  <div>
                    <p className='text-sm text-gray-500'>الأداء</p>
                    <p className='font-semibold'>{selectedDelegate.performance ? selectedDelegate.performance + '%' : '-'}</p>
                  </div>
                </div>
                <div className='flex gap-3 pt-4'>
                  <Button onClick={() => { setShowViewModal(false); openEditModal(selectedDelegate); }} className='flex-1 bg-orange-500 hover:bg-orange-600'>
                    <Edit className='w-4 h-4 ml-2' />
                    تعديل البيانات
                  </Button>
                  <Button onClick={() => setShowViewModal(false)} variant='outline' className='flex-1'>إغلاق</Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
